import { useEffect, useState } from 'react'
import { useStyletron } from 'baseui'
import { useTranslation } from 'react-i18next'
import { InfoDepartmentOverrides, ProviderOverrides } from './styled-components'
import { ChevronDown, ChevronUp } from 'baseui/icon'
import { useDispatch, useSelector } from 'react-redux'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import retrieveAdditionalSalesIcons from '@/shared/icons/retrieveAdditionalSalesIcons'
import { selectAllAdditionalSaleData, selectConfig } from '../../SubscriptionForm/SubscriptionFormSelectors'
import useAdditionalSaleFormData from './useAdditionalSaleFormData'
import {
  selectAdditionalSalesConfig,
  selectAdditionalSalesConfigByName,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { toggleGroupVisibility } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { selectAuthData } from '../../../Login/AuthSelectors'

const useAdditionalSalesContainer = () => {
  const { t } = useTranslation()
  const [, theme] = useStyletron()
  const dispatch = useDispatch()
  const [searchValue, setSearchValue] = useState('')
  const { additionalSaleTabNameToScroll } = useSelector(selectAdditionalSalesConfig)
  const [activeTab, setActiveTab] = useState(additionalSaleTabNameToScroll || 'leadAssistance')
  const { salesConfigLoaded, groupConfig, sales: additionalSales } = useSelector(selectAdditionalSalesConfig)
  const { contactId: id } = useSelector(selectConfig)
  const { country } = useSelector(selectAuthData)
  const selectedSales = Object.keys(additionalSales).filter(key => additionalSales[key]?.zohoId)
  const { updateLead } = useAdditionalSaleFormData()
  const existingSales = useSelector(selectAllAdditionalSaleData)
  const hasZohoIdAdditionalSale = key => {
    return existingSales[key]?.zohoContractDetailId?.length > 0
  }
  // todo-v2: only BE remaining
  /* const vaPrioritiesKeys = [
    'leadFloraAssuranceEthias',
    'leadFloraAssuranceLead',
    'leadYagoAssuranceHouse',
    'leadYagoAssuranceCar',
    'leadYagoAssuranceMoto',
    'leadSantevetAssurance',
    'leadVerisureTelecomBe',
    'leadBoilerEneco',
  ]
  const leadPrioritiesKeys = [
    'leadEthias',
    'leadVerisure',
    'leadBankinAssurance',
    'leadElite',
    'leadAssuranceDefault',
  ] */

  useEffect(() => {
    if (additionalSaleTabNameToScroll) {
      setActiveTab(additionalSaleTabNameToScroll)
    }
  }, [additionalSaleTabNameToScroll])

  const renderTabTitle = (text, isVisible) => {
    const arrowIcon = isVisible ? <ChevronDown size={24} /> : <ChevronUp size={24} />

    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {text}
        {arrowIcon}
      </div>
    )
  }

  const { formIsLoaded, zohoIsInValid } = useFormStatus()
  const hideComponent = !formIsLoaded || !country || zohoIsInValid || !salesConfigLoaded

  const hasValue = object => {
    return object && Object.values(object).some(value => value !== null && value !== '' && value !== false)
  }

  const getLogoColor = key => {
    if (existingSales[key]?.zohoContractDetailId?.length > 0) {
      return theme.colors.positive
    }
    if (hasValue(existingSales[key])) {
      return theme.colors.warning
    }

    return activeTab === key ? theme.colors.primary : theme.colors.mono800
  }

  const getSaleTab = key => {
    return (
      <>
        {retrieveAdditionalSalesIcons(additionalSales[key]?.icon, 20, InfoDepartmentOverrides())}
        {retrieveAdditionalSalesIcons(key, 20, ProviderOverrides(), getLogoColor(key))}
        <div dangerouslySetInnerHTML={{ __html: '&nbsp;' + t('additionalSales:titles.' + key) }} />
      </>
    )
  }

  const updateGroupVisibility = group => {
    dispatch(toggleGroupVisibility(group))
  }

  const { zohoSaleType, isBlockedIfMcpCreated = false } = useSelector(selectAdditionalSalesConfigByName(activeTab))

  useEffect(() => {
    if (!id) return
    window.Echo.channel(`additional-sale-v2.${id}`).listen('.additional-sale.status', e => {
      const {
        zoho_contract_detail_id: zohoContractDetailId,
        zoho_contract_id: zohoContractId,
        status_code: statusCode,
        error_partner_sync: errorPartnerSync,
        sale_type: saleType,
        partner_id: partnerId,
        status_description: statusDescription,
        saleDatabaseId,
        attributes,
      } = e
      const leadStatusData = zohoContractDetailId
        ? { actionsInProgress: false, progressBarValue: 0, state: t('additionalSales:callback_state.end') }
        : {}

      const dataToUpdate = {
        infoAttributes: attributes ?? {},
        statusCode,
        errorPartnerSync,
        partnerId,
        statusDescription,
        zohoContractDetailId,
        zohoContractId,
        saleDatabaseId,
        ...leadStatusData,
      }

      updateLead(dataToUpdate, saleType)

      return () => {
        window.Echo.leaveChannel(`additional-sale-v2.${id}`)
      }
    })
  }, [id])

  return {
    t,
    getSaleTab,
    selectedSales,
    hideComponent,
    activeTab,
    setActiveTab,
    hasZohoIdAdditionalSale,
    renderTabTitle,
    groupConfig,
    updateGroupVisibility,
    zohoSaleType,
    isBlockedIfMcpCreated,
    searchValue,
    setSearchValue,
  }
}

export default useAdditionalSalesContainer
